import React, {useEffect, useMemo, useState} from 'react';
import {
  IonAccordion,
  IonAccordionGroup,
  IonBadge,
  IonButton,
  IonChip,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {searchOutline} from 'ionicons/icons';
import {post} from '../../utils/frRequest';
import {ItemParcial, ItensParciaisPorTipo, TipoItemParcial, TransferenciaParcialSelecao} from '../../models/TransferenciaParcial';
import FiltroPill from '../../components/FiltroPill';
import styles from './SelecionarItensParciaisPage.module.css';

const TIPOS: {chave: TipoItemParcial; label: string}[] = [
  {chave: 'fazendas', label: 'Fazendas'},
  {chave: 'lojas', label: 'Lojas'},
  {chave: 'veterinarios', label: 'Veterinários'},
];

type Acao = 'transferir' | 'desativar';
type SelecaoEstado = Record<TipoItemParcial, {transferir: Set<string>; desativar: Set<string>}>;

const selecaoVazia = (): SelecaoEstado => ({
  fazendas: {transferir: new Set(), desativar: new Set()},
  lojas: {transferir: new Set(), desativar: new Set()},
  veterinarios: {transferir: new Set(), desativar: new Set()},
});

interface SelecionarItensParciaisPageProps {
  deUid: string;
  deNome: string;
  onAvancar: (selecao: TransferenciaParcialSelecao) => void;
  onVoltar: () => void;
}

const SelecionarItensParciaisPage: React.FC<SelecionarItensParciaisPageProps> = ({
  deUid, deNome, onAvancar, onVoltar,
}) => {
  const [itens, setItens] = useState<ItensParciaisPorTipo | null>(null);
  const [selecionados, setSelecionados] = useState<SelecaoEstado>(selecaoVazia());
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [cidade, setCidade] = useState('');

  useEffect(() => {
    let ativo = true;
    setIsLoading(true);
    setErro('');
    post('/veApi/transferencias/listar_itens_parcial', {de_uid: deUid})
      .then(res => {
        if (!ativo) return;
        setIsLoading(false);
        if (!res.success) {
          setErro(res.error || 'Ocorreu um erro ao carregar os itens.');
          return;
        }
        setItens(res.data as ItensParciaisPorTipo);
      });
    return () => {
      ativo = false;
    };
  }, [deUid]);

  const cidades = useMemo(() => {
    if (!itens) return [];
    const encontradas = new Set<string>();
    TIPOS.forEach(({chave}) => {
      itens[chave].forEach(item => {
        if (item.cidade) encontradas.add(item.cidade);
      });
    });
    return Array.from(encontradas).sort((a, b) => a.localeCompare(b, 'pt-BR', {sensitivity: 'base'}));
  }, [itens]);

  const itensFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const porTipo: Record<TipoItemParcial, ItemParcial[]> = {fazendas: [], lojas: [], veterinarios: []};
    if (!itens) return porTipo;
    TIPOS.forEach(({chave}) => {
      porTipo[chave] = itens[chave].filter(item => (
        (!termo || item.nome.toLowerCase().includes(termo))
        && (!cidade || item.cidade === cidade)
      ));
    });
    return porTipo;
  }, [itens, busca, cidade]);

  const escolherAcao = (tipo: TipoItemParcial, itemId: string, acao: Acao) => {
    setSelecionados(atual => {
      const transferir = new Set(atual[tipo].transferir);
      const desativar = new Set(atual[tipo].desativar);
      const jaEstavaNaAcao = (acao === 'transferir' ? transferir : desativar).has(itemId);

      transferir.delete(itemId);
      desativar.delete(itemId);
      if (!jaEstavaNaAcao) {
        (acao === 'transferir' ? transferir : desativar).add(itemId);
      }

      return {...atual, [tipo]: {transferir, desativar}};
    });
  };

  const marcarFiltradosEmMassa = (acao: Acao) => {
    setSelecionados(atual => {
      const novo: SelecaoEstado = {
        fazendas: {transferir: new Set(atual.fazendas.transferir), desativar: new Set(atual.fazendas.desativar)},
        lojas: {transferir: new Set(atual.lojas.transferir), desativar: new Set(atual.lojas.desativar)},
        veterinarios: {
          transferir: new Set(atual.veterinarios.transferir),
          desativar: new Set(atual.veterinarios.desativar),
        },
      };
      TIPOS.forEach(({chave}) => {
        itensFiltrados[chave].forEach(item => {
          novo[chave].transferir.delete(item.id);
          novo[chave].desativar.delete(item.id);
          novo[chave][acao].add(item.id);
        });
      });
      return novo;
    });
  };

  const totalSelecionado = TIPOS.reduce(
    (total, {chave}) => total + selecionados[chave].transferir.size + selecionados[chave].desativar.size, 0);

  const totalFiltrado = TIPOS.reduce((total, {chave}) => total + itensFiltrados[chave].length, 0);

  const handleAvancar = () => {
    onAvancar({
      fazendas: {
        transferir: Array.from(selecionados.fazendas.transferir),
        desativar: Array.from(selecionados.fazendas.desativar),
      },
      lojas: {
        transferir: Array.from(selecionados.lojas.transferir),
        desativar: Array.from(selecionados.lojas.desativar),
      },
      veterinarios: {
        transferir: Array.from(selecionados.veterinarios.transferir),
        desativar: Array.from(selecionados.veterinarios.desativar),
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10">
        <IonSpinner/>
      </div>
    );
  }

  if (erro && !itens) {
    return (
      <div>
        <IonNote color="danger">{erro}</IonNote>
        <div className="mt-4">
          <IonButton fill="outline" onClick={onVoltar}>Voltar</IonButton>
        </div>
      </div>
    );
  }

  return (
    <div>
      <IonText>
        Selecione, de cada lista abaixo, os itens de <strong>{deNome}</strong> que deseja transferir para o
        destinatário ou desativar. Itens não selecionados permanecem intactos.
      </IonText>

      <div className="flex flex-nowrap items-center gap-2 overflow-x-auto py-2 mt-2">
        <div className="flex items-center gap-1 rounded-full border border-gray-300 bg-white shadow-sm text-sm px-3 flex-1 min-w-[160px]">
          <IonIcon icon={searchOutline} className="text-gray-400"/>
          <IonInput
            value={busca}
            placeholder="Buscar por nome..."
            debounce={20}
            onIonChange={e => setBusca(e.detail.value || '')}
          />
        </div>

        <FiltroPill label="Cidade">
          <IonSelect
            class="min-w-0"
            interface="popover"
            placeholder="Todas"
            value={cidade}
            onIonChange={e => setCidade(e.detail.value || '')}
          >
            <IonSelectOption value="">Todas</IonSelectOption>
            {cidades.map(c => (
              <IonSelectOption key={c} value={c}>{c}</IonSelectOption>
            ))}
          </IonSelect>
        </FiltroPill>
      </div>

      <div className="flex flex-col gap-2 mt-2">
        <IonButton
          size="small"
          fill="outline"
          className={`w-full ${styles.botaoMarcarFiltrados}`}
          disabled={totalFiltrado === 0}
          onClick={() => marcarFiltradosEmMassa('transferir')}
        >
          Marcar filtrados p/ Transferir ({totalFiltrado})
        </IonButton>
        <IonButton
          size="small"
          fill="outline"
          color="danger"
          className={`w-full ${styles.botaoMarcarFiltrados}`}
          disabled={totalFiltrado === 0}
          onClick={() => marcarFiltradosEmMassa('desativar')}
        >
          Marcar filtrados p/ Desativar
        </IonButton>
      </div>

      <IonAccordionGroup multiple className="mt-2">
        {TIPOS.map(({chave, label}) => {
          const totalTipo = selecionados[chave].transferir.size + selecionados[chave].desativar.size;
          return (
            <IonAccordion key={chave} value={chave}>
              <IonItem slot="header">
                <IonLabel>
                  {label}
                  {totalTipo > 0 ? ` (${totalTipo} selecionado(s))` : ''}
                </IonLabel>
              </IonItem>
              <IonList slot="content">
                {itensFiltrados[chave].length === 0 && (
                  <IonItem>
                    <IonLabel color="medium">Nenhum item encontrado.</IonLabel>
                  </IonItem>
                )}
                {itensFiltrados[chave].map(item => {
                  const marcadoTransferir = selecionados[chave].transferir.has(item.id);
                  const marcadoDesativar = selecionados[chave].desativar.has(item.id);
                  return (
                    <IonItem key={item.id}>
                      <div className={styles.linha}>
                        <IonLabel className={styles.nome}>
                          <div>{item.nome}</div>
                          {item.cidade && <div className="text-xs text-gray-500">{item.cidade}</div>}
                          {item.ativo === false &&
                            <IonBadge color="danger" className="mt-1 mr-1">
                              Desativado
                            </IonBadge>
                          }
                          {item.transferido_de &&
                            <IonBadge color="warning" className="mt-1">
                              Transferido de {item.transferido_de_nome}
                            </IonBadge>
                          }
                        </IonLabel>
                        <div className={styles.acoes}>
                          <IonChip
                            outline={!marcadoTransferir}
                            color={marcadoTransferir ? 'primary' : undefined}
                            onClick={() => escolherAcao(chave, item.id, 'transferir')}
                          >
                            <IonLabel>Transferir</IonLabel>
                          </IonChip>
                          <IonChip
                            outline={!marcadoDesativar}
                            color={marcadoDesativar ? 'danger' : undefined}
                            onClick={() => escolherAcao(chave, item.id, 'desativar')}
                          >
                            <IonLabel>Desativar</IonLabel>
                          </IonChip>
                        </div>
                      </div>
                    </IonItem>
                  );
                })}
              </IonList>
            </IonAccordion>
          );
        })}
      </IonAccordionGroup>

      <div className="mt-4 flex gap-2">
        <IonButton fill="outline" onClick={onVoltar} className="flex-1">
          Voltar
        </IonButton>
        <IonButton onClick={handleAvancar} disabled={totalSelecionado === 0} className="flex-1">
          Avançar {totalSelecionado > 0 ? `(${totalSelecionado})` : ''}
        </IonButton>
      </div>
    </div>
  );
};

export default SelecionarItensParciaisPage;
